/**
 * FP3-032D — In-app Supporting Documents viewer.
 *
 * Opens an authenticated preview of a single student document in a
 * calm modal so providers can review evidence without downloading.
 *
 * Privacy / security invariants (do NOT relax):
 *   - Bytes are fetched via the authenticated `/download` endpoint
 *     using the same cookie/session the rest of the app uses.
 *   - We never expose the storage path or storage key.
 *   - We never construct or share a public URL.
 *   - The blob object URL is revoked on close / unmount so cached
 *     bytes cannot be smuggled out of the page lifecycle.
 *   - admin_only and deleted docs are blocked server-side; we
 *     surface those as calm "no access" / "not found" copy.
 *
 * Preview support:
 *   - PDF     → <iframe src={objectUrl}> (browser-native PDF renderer)
 *   - PNG/JPG → <img src={objectUrl}>
 *   - TXT     → readable <pre>, truncated past `TEXT_PREVIEW_LIMIT`
 *   - CSV     → table preview of first `CSV_ROW_LIMIT` rows
 *   - DOC/DOCX/other → calm "Preview not available yet" panel
 *
 * Always offers a Download button which streams the same blob bytes
 * we already loaded — no second authenticated round-trip needed.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Download,
  FileText,
  ImageSquare,
  FilePdf,
  Warning,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const TEXT_PREVIEW_LIMIT = 200 * 1024; // 200 KB
const CSV_ROW_LIMIT = 100;
const PREVIEWABLE = new Set(["pdf", "png", "jpg", "jpeg", "txt", "csv"]);

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function csvSplit(line) {
  // Lightweight CSV row split — handles plain commas and "quoted, fields".
  // Not a full RFC-4180 parser, intentionally — we only need a
  // readable preview, not perfect round-trip.
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") { out.push(cur); cur = ""; }
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}

export default function DocumentViewerModal({
  studentId,
  document,
  onClose,
  onDeleted,
}) {
  const ext = (document?.file_extension || "").toLowerCase();
  const canPreview = PREVIEWABLE.has(ext);

  const [phase, setPhase] = useState("loading"); // loading | ready | error | unsupported
  const [errorMsg, setErrorMsg] = useState("");
  const [errorKind, setErrorKind] = useState(""); // forbidden | not_found | server | network
  const [objectUrl, setObjectUrl] = useState("");
  const [textBody, setTextBody] = useState("");
  const [textTruncated, setTextTruncated] = useState(false);
  const [csvRows, setCsvRows] = useState(null); // array of arrays or null

  // Fetch blob once when the viewer opens. Authenticated cookie comes
  // from axios's `withCredentials = true` default. If the file type
  // isn't previewable we skip the fetch entirely and let the user
  // download instead.
  useEffect(() => {
    if (!document?.id || !studentId) return;
    if (!canPreview) {
      setPhase("unsupported");
      return;
    }
    let cancelled = false;
    let createdUrl = "";
    setPhase("loading");
    setErrorMsg("");
    setErrorKind("");

    (async () => {
      try {
        const res = await api.downloadStudentDocument(studentId, document.id);
        if (cancelled) return;
        const blob = res.data;
        if (ext === "txt" || ext === "csv") {
          // Read as text. Size cap so a runaway log file doesn't lock
          // the modal.
          const slice =
            blob.size > TEXT_PREVIEW_LIMIT
              ? blob.slice(0, TEXT_PREVIEW_LIMIT)
              : blob;
          const text = await slice.text();
          if (cancelled) return;
          if (ext === "csv") {
            const rows = text
              .split(/\r?\n/)
              .slice(0, CSV_ROW_LIMIT)
              .filter((line, i, arr) =>
                line.length > 0 || i < arr.length - 1
              )
              .map(csvSplit);
            setCsvRows(rows);
          } else {
            setTextBody(text);
          }
          setTextTruncated(blob.size > TEXT_PREVIEW_LIMIT);
          setPhase("ready");
        } else {
          createdUrl = window.URL.createObjectURL(blob);
          setObjectUrl(createdUrl);
          setPhase("ready");
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        let kind = "server";
        let msg = "Preview unavailable.";
        if (status === 401 || status === 403) {
          kind = "forbidden";
          msg = "You do not have access to this document.";
        } else if (status === 404) {
          kind = "not_found";
          msg = "This document was not found. It may have been deleted.";
        } else if (!err?.response) {
          kind = "network";
          msg = "Preview unavailable — network error. Try downloading instead.";
        }
        setErrorKind(kind);
        setErrorMsg(msg);
        setPhase("error");
        // eslint-disable-next-line no-console
        console.error("[DocumentsUpload] viewer fetch failed", {
          status,
          detail: err?.response?.data?.detail,
          document_id: document.id,
        });
      }
    })();

    return () => {
      cancelled = true;
      // Always revoke any object URL we created so blob bytes do not
      // leak past the viewer's lifetime.
      if (createdUrl) {
        try { window.URL.revokeObjectURL(createdUrl); } catch (_) { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, document?.id]);

  // Also revoke when this component unmounts via the parent removing it.
  useEffect(() => {
    return () => {
      if (objectUrl) {
        try { window.URL.revokeObjectURL(objectUrl); } catch (_) { /* noop */ }
      }
    };
  }, [objectUrl]);

  const downloadAgain = async () => {
    try {
      const res = await api.downloadStudentDocument(studentId, document.id);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = document.file_name_original || document.document_title;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not download"
      );
    }
  };

  const headerIcon = useMemo(() => {
    if (ext === "pdf") return FilePdf;
    if (ext === "png" || ext === "jpg" || ext === "jpeg") return ImageSquare;
    return FileText;
  }, [ext]);
  const HeaderIcon = headerIcon;

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="document-viewer-modal"
        aria-describedby={undefined}
        className="max-w-4xl w-[92vw] h-[88vh] p-0 border-[#E5E3DB] rounded-2xl flex flex-col overflow-hidden"
      >
        <header className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-[#F1F2F4] shrink-0">
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-[15px] font-semibold text-[#1F2937] heading-font flex items-center gap-2 truncate">
              <HeaderIcon
                size={16}
                weight="duotone"
                className="text-[#1E3F33] shrink-0"
              />
              <span
                data-testid="document-viewer-title"
                className="truncate"
              >
                {document.document_title || document.file_name_original}
              </span>
            </DialogTitle>
            <div className="text-[11px] text-[#6B7280] mt-1 flex items-center gap-1.5 flex-wrap">
              <span>{document.document_type}</span>
              <span className="text-[#D6D3CB]">·</span>
              <span className="font-mono uppercase">.{ext || "?"}</span>
              <span className="text-[#D6D3CB]">·</span>
              <span>{fmtBytes(document.file_size_bytes)}</span>
              {document.uploaded_by_name && (
                <>
                  <span className="text-[#D6D3CB]">·</span>
                  <span>
                    Uploaded by {document.uploaded_by_name}
                    {document.uploaded_by_role
                      ? ` · ${document.uploaded_by_role}`
                      : ""}
                  </span>
                </>
              )}
              {document.document_date && (
                <>
                  <span className="text-[#D6D3CB]">·</span>
                  <span>{document.document_date}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 mr-8">
            <Button
              type="button"
              data-testid="document-viewer-download"
              variant="outline"
              size="sm"
              onClick={downloadAgain}
              className="h-8 text-[12px] border-[#E5E3DB] text-[#1F2937]"
            >
              <Download size={12} className="mr-1" />
              Download
            </Button>
          </div>
        </header>

        <div
          className="flex-1 overflow-auto bg-[#FBFAF6]"
          data-testid="document-viewer-body"
        >
          {phase === "loading" && (
            <div
              data-testid="document-viewer-loading"
              className="h-full flex items-center justify-center text-[12.5px] text-[#6B7280]"
            >
              Loading preview…
            </div>
          )}

          {phase === "error" && (
            <div
              data-testid="document-viewer-error"
              data-error-kind={errorKind}
              className="h-full flex flex-col items-center justify-center p-6 text-center"
            >
              <div className="rounded-full p-3 bg-[#F8E8E2] text-[#7A3A2C] mb-3">
                <Warning size={18} weight="duotone" />
              </div>
              <p className="text-[13px] font-semibold text-[#1F2937] heading-font">
                {errorMsg}
              </p>
              <p className="text-[11.5px] text-[#6B7280] mt-1">
                You can try downloading the file directly.
              </p>
              <Button
                type="button"
                data-testid="document-viewer-error-download"
                size="sm"
                onClick={downloadAgain}
                className="mt-3 h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                <Download size={12} className="mr-1" />
                Download instead
              </Button>
            </div>
          )}

          {phase === "unsupported" && (
            <div
              data-testid="document-viewer-unsupported"
              className="h-full flex flex-col items-center justify-center p-6 text-center"
            >
              <div className="rounded-full p-3 bg-[#F1F2F4] text-[#3F4756] mb-3">
                <FileText size={18} weight="duotone" />
              </div>
              <p className="text-[13px] font-semibold text-[#1F2937] heading-font">
                Preview isn’t available for this file type yet.
              </p>
              <p className="text-[11.5px] text-[#6B7280] mt-1 max-w-md">
                .{ext || "this format"} documents can’t be previewed in
                Frame Practice in this release. You can still download
                the file securely below.
              </p>
              <Button
                type="button"
                data-testid="document-viewer-unsupported-download"
                size="sm"
                onClick={downloadAgain}
                className="mt-3 h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                <Download size={12} className="mr-1" />
                Download document
              </Button>
            </div>
          )}

          {phase === "ready" && ext === "pdf" && (
            <iframe
              data-testid="document-viewer-pdf"
              title={document.document_title || "Document preview"}
              src={objectUrl}
              className="w-full h-full bg-white"
            />
          )}

          {phase === "ready" && (ext === "png" || ext === "jpg" || ext === "jpeg") && (
            <div className="h-full flex items-center justify-center p-4">
              <img
                data-testid="document-viewer-image"
                alt={document.document_title || "Document preview"}
                src={objectUrl}
                className="max-w-full max-h-full object-contain rounded-md shadow-sm bg-white"
              />
            </div>
          )}

          {phase === "ready" && ext === "txt" && (
            <div className="p-5">
              {textTruncated && (
                <p
                  data-testid="document-viewer-truncated"
                  className="text-[11px] text-[#9C6B2E] bg-[#FDF8F3] border border-[#E9D9A9] rounded-md px-3 py-1.5 mb-3"
                >
                  Showing first {Math.round(TEXT_PREVIEW_LIMIT / 1024)} KB. Download
                  to see the full file.
                </p>
              )}
              <pre
                data-testid="document-viewer-text"
                className="text-[12px] text-[#1F2937] whitespace-pre-wrap font-mono leading-relaxed bg-white border border-[#E5E3DB] rounded-md p-4"
              >
                {textBody}
              </pre>
            </div>
          )}

          {phase === "ready" && ext === "csv" && (
            <div className="p-5">
              {textTruncated && (
                <p
                  data-testid="document-viewer-truncated"
                  className="text-[11px] text-[#9C6B2E] bg-[#FDF8F3] border border-[#E9D9A9] rounded-md px-3 py-1.5 mb-3"
                >
                  Showing first {Math.round(TEXT_PREVIEW_LIMIT / 1024)} KB of the
                  file ({CSV_ROW_LIMIT} rows max). Download to see the full
                  CSV.
                </p>
              )}
              <div className="bg-white border border-[#E5E3DB] rounded-md overflow-x-auto">
                <table
                  data-testid="document-viewer-csv"
                  className="w-full text-[12px] text-[#1F2937] border-collapse"
                >
                  <tbody>
                    {(csvRows || []).map((row, ri) => (
                      <tr
                        key={ri}
                        className={ri === 0 ? "bg-[#F3F1EC]" : ri % 2 ? "bg-[#FBFAF6]" : ""}
                      >
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={`px-3 py-1.5 border-b border-[#F1F2F4] align-top ${
                              ri === 0 ? "font-semibold text-[#3F4756]" : ""
                            }`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
